<template>
  <v-container fluid>
    <h2 class="page-title mb-4">Ranking de servicios</h2>

    <!-- Filtro de país -->
    <v-row class="mt-2" align="center">
      <v-col cols="12" sm="6" md="4">
        <v-select
          v-model="selectedIsos"
          :items="countryOptions"
          item-text="text"
          item-value="value"
          label="País"
          multiple
          chips
          small-chips
          dense
          hide-details
          outlined
          @change="onCountryChange"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="visibleRows"
          :loading="loadingData"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [10, 20, 30, 50, 100] }"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="elevation-1"
        >
          <template v-slot:item.usageRate="{ item }">
            {{ formatRate(item.usageRate) }}
          </template>
          <template v-slot:item.avgRequestedAmount="{ item }">
            {{ formatAmount(item.avgRequestedAmount, item.currency) }}
          </template>
          <template v-slot:item.avgApprovedAmount="{ item }">
            {{ formatAmount(item.avgApprovedAmount, item.currency) }}
          </template>
          <template v-slot:item.modeRequestedAmount="{ item }">
            {{ formatAmount(item.modeRequestedAmount, item.currency) }}
          </template>
          <template v-slot:item.approvedRefunds="{ item }">
            <v-chip x-small color="primary" dark>{{ item.approvedRefunds }}</v-chip>
          </template>
          <template v-slot:no-data>
            <div class="py-4">
              {{ selectedIsos.length ? 'Sin resultados para los filtros seleccionados.' : 'Selecciona al menos un país.' }}
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import ServiceRankingService from '../services/service-ranking.service';

export default {
  name: 'ServiceRanking',
  mixins: [utilsMixin],
  data() {
    return {
      countryOptions: [
        { text: 'Chile', value: 'CL' },
        { text: 'Perú', value: 'PE' },
        { text: 'México', value: 'MX' }
      ],
      selectedIsos: ['CL'],
      rows: [],
      loadingData: false,
      sortBy: 'usageRate',
      sortDesc: true,
      headers: [
        { text: 'Servicio', value: 'serviceTitle' },
        {
          text: 'Tasa de uso',
          value: 'usageRate',
          // Trata null como -Infinity para que los servicios sin exposición queden al final (desc).
          sort: this.nullsLastSort
        },
        { text: '# Planes disponibles', value: 'plansAvailable' },
        { text: '# Reembolsos aprobados (12m)', value: 'approvedRefunds' },
        { text: 'Monto promedio solicitado', value: 'avgRequestedAmount', sort: this.nullsLastSort },
        { text: 'Monto promedio aprobado', value: 'avgApprovedAmount', sort: this.nullsLastSort },
        { text: 'Monto moda solicitado', value: 'modeRequestedAmount', sort: this.nullsLastSort }
      ]
    };
  },
  computed: {
    // Oculta servicios sin uso (0 reembolsos aprobados en la ventana).
    visibleRows() {
      return this.rows.filter(r => r.approvedRefunds > 0);
    }
  },
  mounted() {
    this.loadRanking();
  },
  methods: {
    onCountryChange() {
      this.loadRanking();
    },

    async loadRanking() {
      if (this.selectedIsos.length === 0) {
        this.rows = [];
        return;
      }
      this.loadingData = true;
      try {
        const res = await ServiceRankingService.getServiceRanking(this.selectedIsos);
        if (res.status === 'ok') {
          this.rows = res.data.rows;
        } else {
          this.showErrorSnackbar('No se pudo cargar el ranking de servicios');
        }
      } catch {
        this.showErrorSnackbar('No se pudo cargar el ranking de servicios');
      }
      this.loadingData = false;
    },

    formatRate(rate) {
      if (rate === null || rate === undefined) return '—';
      return (Number(rate) * 100).toFixed(2) + '%';
    },

    formatAmount(value, currency) {
      if (value === null || value === undefined) return '—';
      const cur = currency || 'CLP';
      try {
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: cur,
          maximumFractionDigits: 0
        }).format(Number(value));
      } catch {
        return `${Number(value)} ${cur}`;
      }
    },

    // Ordena tratando null/undefined como -Infinity (quedan al final en desc).
    nullsLastSort(a, b) {
      const va = a === null || a === undefined ? -Infinity : a;
      const vb = b === null || b === undefined ? -Infinity : b;
      return va - vb;
    }
  }
};
</script>

<style scoped>
.page-title {
  color: #104b94;
  font-weight: 700;
}
</style>
